import { MEDIA_CACHE_NAME_SW } from '../config';
import { IS_REST_PROVIDER, REST_BACKEND_URL } from '../api/rest/config';

// Main-thread builder for a same-origin media-proxy URL usable directly as an `<img src>`. This
// mirrors the API worker's `cdnUrlForHash` + `toProxiedCdnUrl` (src/api/rest/mediaRegistry.ts,
// src/api/rest/files.ts) so static images (avatars) can render natively — the browser streams,
// decodes and HTTP-caches them — instead of round-tripping the worker as a Blob via `downloadMedia`.

const MAX_DPR = 2;
const AVATAR_WIDTH = 160;
const PROFILE_WIDTH = 640;

// CDN width transform, matching the worker's `appendWidth`. `devicePixelRatio` is read directly
// here (the main thread has `window`, unlike the worker which is handed the value at init).
function appendWidth(cdnUrl: string, width: number): string {
  const rawDpr = typeof window !== 'undefined' && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
  const dpr = Math.min(rawDpr, MAX_DPR);
  const scaledWidth = Math.round(width * dpr);
  return cdnUrl.includes('?') ? `${cdnUrl}&w=${scaledWidth}` : `${cdnUrl}?w=${scaledWidth}`;
}

function toProxyUrl(cdnUrl: string): string {
  return `${REST_BACKEND_URL}/media/proxy?u=${encodeURIComponent(cdnUrl)}`;
}

// Native proxy URL for a peer avatar CDN url, or `undefined` when not on the REST provider / no url.
export function getAvatarProxyUrl(cdnUrl: string | undefined, isBig = false): string | undefined {
  if (!IS_REST_PROVIDER || !cdnUrl) return undefined;
  return toProxyUrl(appendWidth(cdnUrl, isBig ? PROFILE_WIDTH : AVATAR_WIDTH));
}

// Per-target CDN widths for message photos, mirroring `getPhotoMediaHash` size letters →
// `cdnUrlForHash` widths (m = 320, x = 800). `full`/`download` are absent on purpose: those map to
// the unsized hash, which `cdnUrlForHash` serves as the original file — so the full-screen viewer
// gets full resolution, matching the worker path.
const PHOTO_WIDTH_BY_TARGET: Record<string, number> = {
  micro: 320, pictogram: 320, inline: 800, preview: 800,
};

// Native proxy URL for a message photo at a given display target, or `undefined` when not on the
// REST provider / no url. GIFs are served unresized (the CDN rejects `?w=` on them, mirroring
// `cdnUrlForHash`). Unknown/`full` targets serve the original file (no `?w=`).
export function getPhotoProxyUrl(cdnUrl: string | undefined, target: string): string | undefined {
  if (!IS_REST_PROVIDER || !cdnUrl) return undefined;
  const width = cdnUrl.endsWith('.gif') ? undefined : PHOTO_WIDTH_BY_TARGET[target];
  return toProxyUrl(width ? appendWidth(cdnUrl, width) : cdnUrl);
}

// Native proxy URL for a static sticker, custom emoji, or static reaction icon (webp/png), or
// `undefined` when not on the REST provider / no url. Served unsized to match the worker's
// `cdnUrlForHash` for `sticker<id>` and plain `document<id>` (both return the raw file), so it shares
// the browser HTTP cache and never round-trips a Blob — the same reason photos never re-download.
export function getStaticMediaProxyUrl(cdnUrl: string | undefined): string | undefined {
  if (!IS_REST_PROVIDER || !cdnUrl) return undefined;
  return toProxyUrl(cdnUrl);
}

// Proxy URLs (stickers, reactions, custom emoji, message photos) that have decoded at least once this session. A
// re-mounted `<img>` whose URL is here is served straight from the browser HTTP cache and paints
// immediately, so the component can mount it already-"loaded" and skip its open crossfade instead of
// flashing blank-then-fading on every remount — the reload flicker users saw on scroll/chat switch.
const loadedStaticProxyUrls = new Set<string>();

export function hasLoadedStaticProxyUrl(url: string | undefined): boolean {
  return url ? loadedStaticProxyUrls.has(url) : false;
}

export function markStaticProxyUrlLoaded(url: string | undefined): void {
  if (url) loadedStaticProxyUrls.add(url);
}

// The set claims a url paints instantly because the service worker holds it on disk. When that cache
// is emptied the claim becomes false for every entry at once, and the components that trust it skip
// their placeholder and crossfade — so clearing it has to happen in the same breath as clearing the
// cache it was derived from.
export function clearLoadedStaticProxyUrls(): void {
  loadedStaticProxyUrls.clear();
}

// The service worker trims its media cache on its own schedule (every 25 writes), and the page has no
// other way to learn which urls left. Listening here keeps the "already on disk" claim honest without
// re-reading the whole cache.
export function subscribeToMediaCacheTrim(): void {
  navigator.serviceWorker?.addEventListener('message', (event) => {
    const data = event.data as { type?: string; urls?: string[] } | undefined;
    if (data?.type !== 'mediaCacheTrimmed' || !Array.isArray(data.urls)) return;

    data.urls.forEach((url) => loadedStaticProxyUrls.delete(url));
  });
}

// Rebuild the set from the service worker's media cache at boot. Those entries are exactly the proxy
// responses the SW can serve from disk with no network round trip, so a photo or sticker the user
// already saw in an earlier session paints in the very first render pass after a reload — instead of
// the blank-box-then-spinner-then-fade that made every F5 look like a fresh download.
export async function hydrateLoadedStaticProxyUrls(): Promise<void> {
  try {
    const cache = await self.caches.open(MEDIA_CACHE_NAME_SW);
    const keys = await cache.keys();
    keys.forEach((request) => loadedStaticProxyUrls.add(request.url));
  } catch {
    // No Cache Storage (private mode, unsupported browser) — the set stays session-only, as before
  }
}
